/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useInspectionRecordSettings.ts
 * FILE_ID: JOBHUB-QA-RECORD-SETTINGS-HOOK-001
 * PURPOSE: Read and save one QA's record settings.
 * WHY: These fields belong to the inspection, not the job.
 * Reused from: packages/job-hub/src/jobs/tabs/job-qa/useJobQuoteDetails.ts (uploadCover, commitReportTitle, displayedQuoteReference)
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import type { QuoteRow } from '../../jobDetailTypes'
import {
  defaultItpReportTitle,
  resolveItpReportTitle,
} from '../../qa/itpReportTitle'
import { ITP_COVER_IMAGE_DEFAULT_MM, itpCoverImageSizeMm } from '../../qa/itpCover'
import {
  displayedQuoteReference,
  siteLabel,
  type QuoteClientHit,
  type QuoteSiteHit,
} from './useJobQuoteDetails'

export interface CoverPerson {
  name: string
  email: string
  role: string
  phone: string
  userId?: number
}

interface SettingsRow {
  job_title?: string
  quote_reference?: string | null
  client_name?: string
  client_contact_name?: string
  client_address?: string
  client_phone?: string
  client_email?: string
  client_id?: number | null
  site_address?: string
  report_title?: string | null
  itp_report_title?: string | null
  cover_image_url?: string | null
  cover_image_width_mm?: number | null
  cover_image_height_mm?: number | null
  participants?: CoverPerson[]
  include_product?: boolean
  include_requirement?: boolean
  include_acceptance?: boolean
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function messageOf(error: unknown): string {
  const data = (error as { response?: { data?: { detail?: string; error?: string } } })?.response?.data
  return data?.detail || data?.error || (error instanceof Error ? error.message : 'Could not save these settings.')
}

const EMPTY_ROW: SettingsRow = {}

function peopleOf(value: unknown): CoverPerson[] {
  if (!Array.isArray(value)) return []
  return value.map((row) => {
    const item = row as Record<string, unknown>
    const userId = typeof item.userId === 'number' ? item.userId : typeof item.user_id === 'number' ? item.user_id : undefined
    return {
      name: text(item.name),
      email: text(item.email),
      role: text(item.role),
      phone: text(item.phone),
      ...(userId != null ? { userId } : {}),
    }
  })
}

export function useInspectionRecordSettings(
  inspectionId: string,
  jobId: number,
  orgId: number | undefined,
  quotes: readonly QuoteRow[] = [],
  sourceQuoteSheetIds: readonly number[] = [],
) {
  const { client } = useJobHubDataAdapter()
  const queryClient = useQueryClient()
  const url = `/api/qa/inspections/${inspectionId}/record-settings/`
  const { data } = useQuery({
    queryKey: ['qa-record-settings', inspectionId],
    enabled: Boolean(inspectionId),
    queryFn: () => client.get(url).then((response) => response.data as SettingsRow),
  })
  const row = data ?? EMPTY_ROW
  const [jobTitle, setJobTitle] = useState('')
  const [quoteReference, setQuoteReference] = useState('')
  const [clientName, setClientName] = useState('')
  const [siteName, setSiteName] = useState('')
  const [reportTitle, setReportTitle] = useState('')
  const [coverWidthMm, setCoverWidthMm] = useState<number>(ITP_COVER_IMAGE_DEFAULT_MM.width)
  const [coverHeightMm, setCoverHeightMm] = useState<number>(ITP_COVER_IMAGE_DEFAULT_MM.height)
  const [clientReset, setClientReset] = useState(0)
  const [siteReset, setSiteReset] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const savedQuote = useRef<string | null>(null)
  const savedReport = useRef<string | null>(null)
  const quoteDirty = useRef(false)
  const quoteRef = useRef('')
  const clientNameRef = useRef('')
  const loaded = useRef(false)
  const quotesRef = useRef(quotes)
  const sourceRef = useRef(sourceQuoteSheetIds)
  quoteRef.current = quoteReference
  quotesRef.current = quotes
  sourceRef.current = sourceQuoteSheetIds

  useEffect(() => {
    if (!data) return
    loaded.current = true
    const current = data
    const storedTitle = 'report_title' in current ? current.report_title : current.itp_report_title
    setJobTitle(text(current.job_title) || text((current as { display_name?: string }).display_name))
    const quote = displayedQuoteReference(current.quote_reference, quotesRef.current, sourceRef.current)
    savedQuote.current = current.quote_reference ?? null
    if (!quoteDirty.current) setQuoteReference(quote)
    const name = text(current.client_name)
    clientNameRef.current = name
    setClientName(name)
    setSiteName(text(current.site_address))
    savedReport.current = storedTitle ?? null
    setReportTitle(resolveItpReportTitle(storedTitle, name))
    const size = itpCoverImageSizeMm(current.cover_image_width_mm, current.cover_image_height_mm)
    setCoverWidthMm(size.width)
    setCoverHeightMm(size.height)
  }, [data, inspectionId])

  const publish = (next: SettingsRow) => {
    queryClient.setQueryData(['qa-record-settings', inspectionId], next)
  }

  const put = async (body: Record<string, unknown>) => {
    const { data: saved } = await client.put<SettingsRow>(url, body)
    setError(null)
    publish({ ...row, ...body, ...saved })
    return saved
  }

  const orgParams = orgId != null ? { organisation: orgId } : {}
  const participants = useMemo(() => peopleOf(row.participants), [row.participants])
  const include = {
    product: Boolean(row.include_product),
    requirement: Boolean(row.include_requirement),
    acceptance: Boolean(row.include_acceptance),
  }
  const setInclude = async (key: 'product' | 'requirement' | 'acceptance', value: boolean) => {
    const field = key === 'product'
      ? 'include_product'
      : key === 'requirement'
        ? 'include_requirement'
        : 'include_acceptance'
    const previous = row
    publish({ ...row, [field]: value })
    try {
      await put({ [field]: value })
    } catch (err) {
      publish(previous)
      setError(messageOf(err))
    }
  }

  return {
    include,
    setInclude,
    jobTitle,
    setJobTitle,
    commitJobTitle: async () => {
      if (!loaded.current || jobTitle === text(row.job_title)) return
      try {
        await put({ job_title: jobTitle })
      } catch (err) {
        setJobTitle(text(row.job_title))
        setError(messageOf(err))
      }
    },
    quoteReference,
    setQuoteReference: (value: string) => {
      quoteDirty.current = true
      quoteRef.current = value
      setQuoteReference(value)
    },
    commitQuoteReference: async () => {
      if (!quoteDirty.current || !loaded.current) return
      quoteDirty.current = false
      const next = quoteRef.current
      try {
        await put({ quote_reference: next })
        savedQuote.current = next
      } catch (err) {
        quoteDirty.current = true
        setError(messageOf(err))
      }
    },
    clientName,
    clientEmail: text(row.client_email),
    clientContactName: text(row.client_contact_name),
    clientAddress: text(row.client_address),
    clientPhone: text(row.client_phone),
    clientReset,
    siteName,
    siteReset,
    reportTitle,
    setReportTitle,
    commitReportTitle: async () => {
      if (!loaded.current) return
      const trimmed = reportTitle.trim()
      const fallback = defaultItpReportTitle(clientNameRef.current)
      const next = !trimmed || trimmed === fallback ? null : trimmed
      if ((savedReport.current ?? null) === next) return
      const previous = savedReport.current
      try {
        await put({ report_title: next })
        savedReport.current = next
      } catch (err) {
        savedReport.current = previous
        setReportTitle(resolveItpReportTitle(previous, clientNameRef.current))
        setError(messageOf(err))
      }
    },
    coverImageUrl: row.cover_image_url ?? null,
    coverWidthMm,
    coverHeightMm,
    setCoverWidthMm,
    setCoverHeightMm,
    commitCoverSize: async () => {
      if (!loaded.current) return
      const next = itpCoverImageSizeMm(coverWidthMm, coverHeightMm)
      setCoverWidthMm(next.width)
      setCoverHeightMm(next.height)
      const stored = itpCoverImageSizeMm(row.cover_image_width_mm, row.cover_image_height_mm)
      if (stored.width === next.width && stored.height === next.height) return
      try {
        await put({ cover_image_width_mm: next.width, cover_image_height_mm: next.height })
      } catch (err) {
        setCoverWidthMm(stored.width)
        setCoverHeightMm(stored.height)
        setError(messageOf(err))
      }
    },
    participants,
    saveParticipants: async (people: CoverPerson[]) => {
      await put({ participants: people })
    },
    uploadCover: async (file: File) => {
      const body = new FormData()
      body.append('cover_image', file)
      const { data: saved } = await client.put<SettingsRow>(url, body)
      publish({ ...row, ...saved })
      return saved.cover_image_url ?? null
    },
    clearCover: async () => {
      const { data: saved } = await client.put<SettingsRow>(url, { cover_image: null })
      publish({ ...row, ...saved, cover_image_url: null })
      return null
    },
    searchClients: async (query: string): Promise<QuoteClientHit[]> => {
      const { data: payload } = await client.get('/api/report/clients/', { params: { ...orgParams, search: query } })
      const results = (payload as { results?: Array<Record<string, unknown>> })?.results ?? []
      return results.map((item) => ({
        id: Number(item.id),
        client_name: text(item.client_name) || undefined,
        company_name: text(item.company_name) || undefined,
        email: text(item.email) || text(item.primary_email) || undefined,
        primary_email: text(item.primary_email) || undefined,
      }))
    },
    createClient: async (name: string): Promise<QuoteClientHit | null> => {
      const { data: created } = await client.post<Record<string, unknown>>('/api/report/clients/', { client_name: name }, { params: orgParams })
      const id = Number(created?.id)
      if (!Number.isFinite(id)) return null
      return { id, client_name: text(created.client_name) || name }
    },
    selectClient: async (hit: QuoteClientHit) => {
      const name = (hit.client_name || hit.company_name || '').trim()
      try {
        await put({
          client_id: hit.id,
          client_name: name,
          client_email: (hit.email || hit.primary_email || '').trim(),
        })
        clientNameRef.current = name
        setClientName(name)
      } catch (err) {
        setClientReset((value) => value + 1)
        setError(messageOf(err))
      }
    },
    searchSites: async (query: string): Promise<QuoteSiteHit[]> => {
      const { data: payload } = await client.get('/api/report/sites/', { params: { ...orgParams, search: query } })
      const results = (payload as { results?: QuoteSiteHit[] })?.results ?? []
      return results
    },
    selectSite: async (hit: QuoteSiteHit) => {
      const label = siteLabel(hit)
      try {
        await put({ site_address: label })
        setSiteName(label)
      } catch (err) {
        setSiteReset((value) => value + 1)
        setError(messageOf(err))
      }
    },
    createSite: async () => null,
    error,
    setError,
    loaded: Boolean(data && ('job_title' in (data as object) || 'participants' in (data as object) || 'cover_image_url' in (data as object))),
    settings: row,
  }
}
